/**
 * 📮 Serviço de CEP - Integração com ViaCEP
 * 
 * Funcionalidades:
 * - Buscar endereço por CEP
 * - Validar formato de CEP
 * - Autocomplete de campos de endereço
 * - Tratamento de erros
 * 
 * API: https://viacep.com.br/
 * Gratuita, sem necessidade de chave
 */

import { useState } from 'react';

// Validar formato de CEP
export const isValidCEP = (cep) => {
  if (!cep) return false;
  
  // Remover caracteres não numéricos
  const cleanCEP = cep.replace(/\D/g, '');
  
  // Verificar se tem exatamente 8 dígitos
  if (cleanCEP.length !== 8) return false;
  
  // Verificar se não é um CEP inválido conhecido
  const invalidCEPs = [
    '00000000',
    '11111111',
    '22222222',
    '33333333',
    '44444444',
    '55555555',
    '66666666',
    '77777777',
    '88888888',
    '99999999'
  ];
  
  return !invalidCEPs.includes(cleanCEP);
};

// Formatar CEP para exibição
export const formatCEP = (cep) => {
  if (!cep) return '';
  
  const cleanCEP = cep.replace(/\D/g, '');
  
  if (cleanCEP.length <= 5) {
    return cleanCEP;
  }
  
  return cleanCEP.replace(/(\d{5})(\d{3})/, '$1-$2');
};

// Buscar endereço por CEP
export const fetchAddressByCEP = async (cep) => {
  try {
    // Validar CEP antes de fazer a requisição
    if (!isValidCEP(cep)) {
      throw new Error('CEP inválido. Verifique o formato (00000-000).');
    }
    
    // Limpar CEP para a requisição
    const cleanCEP = cep.replace(/\D/g, '');
    
    console.log('🔍 Buscando CEP:', cleanCEP);
    
    // Fazer requisição para ViaCEP
    const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
    
    if (!response.ok) {
      throw new Error('Erro na consulta do CEP. Tente novamente.');
    }
    
    const data = await response.json();
    
    // Verificar se o CEP foi encontrado
    if (data.erro) {
      throw new Error('CEP não encontrado. Verifique se está correto.');
    }
    
    console.log('✅ Endereço encontrado:', {
      cep: data.cep,
      logradouro: data.logradouro,
      bairro: data.bairro,
      localidade: data.localidade,
      uf: data.uf
    });
    
    // Retornar dados formatados
    return {
      success: true,
      data: {
        zipCode: formatCEP(data.cep),
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        country: 'Brasil'
      }
    };
    
  } catch (error) {
    console.error('❌ Erro ao buscar CEP:', error.message);
    
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// Hook personalizado para CEP (opcional)
export const useCEPLookup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const lookupCEP = async (cep) => {
    setLoading(true);
    setError(null);
    
    const result = await fetchAddressByCEP(cep);
    
    setLoading(false);
    
    if (!result.success) {
      setError(result.error);
      return null;
    }
    
    return result.data;
  };
  
  return {
    lookupCEP,
    loading,
    error,
    clearError: () => setError(null)
  };
};

// Serviço principal exportado
export const cepService = {
  isValid: isValidCEP,
  format: formatCEP,
  lookup: fetchAddressByCEP
};

export default cepService;
